/**
 * Ops UI API client.
 *
 * Wraps `createApiClient` from `@nova-integration/ui-shared` to get the
 * standard JWT + session-expiry behaviour, then layers ops-ui specific helpers
 * on top: events, mock-PARTNER monitor, and the LiveEventMonitor's loader.
 *
 * Diaper / Sessions / Simulator API helpers used to live here too — they now
 * live in services/diaper-ui/frontend/src/lib/api.ts.
 */
import { createApiClient, ApiError } from '@nova-integration/ui-shared';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const client = createApiClient({ useAuthStore, baseUrl: API_BASE_URL });

export { ApiError };

interface ApiCallOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function apiCall(endpoint: string, options: ApiCallOptions = {}): Promise<Response> {
  return client.call(endpoint, options);
}

/** Useful when a Nova-bound request is forwarded server-side and a specific
 *  account must be picked. Kept here for callers that explicitly choose an
 *  account; the diaper-ui BFF now owns the actual Nova proxy. */
export function novaProxyHeaders(novaAccountId?: string | null): Record<string, string> {
  return novaAccountId ? { 'x-nova-account-id': novaAccountId } : {};
}

export type EventSource = 'partner' | 'poller' | 'all';

export async function loadEvents(options: {
  limit?: number;
  sessionId?: number | string;
  after?: string;
  /** Which event stream to read from. Defaults to 'partner' (outbound deliveries). */
  source?: EventSource;
} = {}): Promise<{
  events: any[];
  count?: number;
  unseen_count?: number;
  organization_id?: number;
  has_more?: boolean;
  requested_limit?: number;
  filters?: { sessionId?: string | null; after?: string | null };
}> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit || 100));
  if (options.sessionId) params.set('sessionId', String(options.sessionId));
  if (options.after) params.set('after', options.after);
  if (options.source) params.set('source', options.source);

  const response = await apiCall(`/api/events?${params.toString()}`);
  if (!response.ok) {
    throw new ApiError('Failed to load events', response.status);
  }
  return response.json();
}

export async function loadMockPartnerEvents(
  options: { limit?: number; organizationId?: string } = {}
): Promise<{ total: number; events: any[]; filterOrganizationId: string | null }> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit || 200));
  if (options.organizationId) params.set('organization_id', options.organizationId);
  const response = await apiCall(`/api/v2/events?${params.toString()}`);
  if (!response.ok) {
    throw new ApiError('Failed to load mock PARTNER events', response.status);
  }
  return response.json();
}

export async function clearMockPartnerEvents(): Promise<{ status: string; deleted: number }> {
  const response = await apiCall('/api/v2/events', { method: 'DELETE' });
  if (!response.ok) {
    throw new ApiError('Failed to clear mock PARTNER events', response.status);
  }
  return response.json();
}

export async function loadMockPartnerStats(): Promise<{
  totalReceived: number;
  accepted: number;
  rejected: number;
  sseClients: number;
  uptime: number;
}> {
  const response = await apiCall('/api/v2/stats');
  if (!response.ok) {
    throw new ApiError('Failed to load mock PARTNER stats', response.status);
  }
  return response.json();
}
